import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiJson } from "../../lib/api/_http";
import { ApiClientError } from "../../lib/api/_errors";

// /claim — authed redemption of a Fam claim-link token. Reads `?token=…` from
// the search params, POSTs to /api/auth/claim (which forwards to the identity
// Worker's /v1/auth/claim → fam /v1/internal/add-member), and on success
// navigates to /fam/:slug.
//
// AuthGuard wraps this route, so by the time we render the session cookie is
// in place and the call below auto-includes credentials. If the redemption
// fails (expired token, role cap exceeded, etc.) we surface the API code +
// message inline with a "Back to dashboard" CTA — no silent failures.

interface ClaimResponse {
	ok: true;
	famId: string;
	famSlug: string | null;
	alreadyMember: boolean;
	role: string;
}

type State =
	| { kind: "claiming" }
	| { kind: "success"; famSlug: string; alreadyMember: boolean }
	| { kind: "error"; code: string; message: string };

export function ClaimRoute() {
	const [params] = useSearchParams();
	const token = params.get("token")?.trim() ?? "";
	const navigate = useNavigate();
	const [state, setState] = useState<State>({ kind: "claiming" });

	useEffect(() => {
		if (!token) {
			setState({
				kind: "error",
				code: "invalid_request",
				message: "Missing invite token.",
			});
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const res = await apiJson<ClaimResponse>("/api/auth/claim", {
					method: "POST",
					body: JSON.stringify({ token }),
				});
				if (cancelled) return;
				// famSlug is nullable in the wire shape because legacy fams could
				// be created without a slug. The post-seed pricing migration backfills
				// slugs for everyone — if it's somehow missing we fall back to /.
				if (res.famSlug) {
					setState({
						kind: "success",
						famSlug: res.famSlug,
						alreadyMember: res.alreadyMember,
					});
					// Brief delay so the user sees the success state instead of an
					// instant route change — feels less like a redirect loop.
					setTimeout(() => {
						void navigate(`/fam/${encodeURIComponent(res.famSlug!)}`, {
							replace: true,
						});
					}, 600);
				} else {
					setTimeout(() => {
						void navigate("/", { replace: true });
					}, 600);
				}
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiClientError) {
					setState({
						kind: "error",
						code: err.code,
						message: err.message,
					});
				} else {
					setState({
						kind: "error",
						code: "network_error",
						message: err instanceof Error ? err.message : "Unknown error",
					});
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token, navigate]);

	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div className="w-full max-w-md">
				{state.kind === "claiming" && (
					<div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elev)] p-8 text-center">
						<h1 className="text-lg font-semibold text-[var(--color-fg)]">
							Joining your Fam…
						</h1>
						<p className="mt-2 text-sm text-[var(--color-muted)]">
							Redeeming your invite.
						</p>
					</div>
				)}

				{state.kind === "success" && (
					<div className="rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/[0.06] p-8 text-center">
						<h1 className="text-lg font-semibold text-[var(--color-fg)]">
							{state.alreadyMember ? "Welcome back" : "You're in"}
						</h1>
						<p className="mt-2 text-sm text-[var(--color-muted)]">
							{state.alreadyMember
								? "You were already a member. Loading the Fam…"
								: "Loading your new Fam…"}
						</p>
					</div>
				)}

				{state.kind === "error" && (
					<div className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/[0.06] p-8 text-center">
						<h1 className="text-lg font-semibold text-[var(--color-fg)]">
							Invite expired or invalid
						</h1>
						<p className="mt-2 text-sm text-[var(--color-muted)]">
							{state.message}
						</p>
						<p className="mt-1 text-xs text-[var(--color-muted)]">
							Code: {state.code}
						</p>
						<div className="mt-6 flex justify-center gap-3 text-sm">
							<Link
								to="/"
								className="rounded-md border border-[var(--color-border)] px-3 py-1.5 hover:border-[var(--color-accent)]"
							>
								Back to dashboard
							</Link>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
